import { useEffect, useMemo, useRef, useState } from 'react';

import { CreateFamilyEvent } from '@src/components/Modals/CreateFamilyEventModal/type';
import { Button, DatePicker, Input, Select, Table, message, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import { openModal } from '@reduce/modals';

import useGetFamilyEvents from '@hooks/queries/event/useGetFamilyEvents';

import { deleteFamilyEvent } from '@api/event';
import type { GetFamilyEventsQuery, GetFamilyEventsResponse } from '@api/event/getFamilyEvents';

import { off, on } from '@utils/globalEvents';

import * as C from '@components/Common';
import Sort from '@components/Sort';
import Text from '@components/Text';

import { ReactComponent as SearchIcon } from '@assets/icons/search.svg';

import type { Get } from '@type/util';

import * as S from './styled';
import type * as T from './type';

const PAGE_SIZE: number = 8;

function FamilyEventTable() {
  const dispatch = useDispatch();

  const containerRef = useRef<HTMLDivElement>(null);

  // antd select dropwdown 을 닫을 때 검색어 초기화 막기위한 ref
  const isInputClear = useRef<boolean>(true);

  const [page, setPage] = useState<number>(1);
  const [dir, setDir] = useState<GetFamilyEventsQuery['dir']>('desc');
  const [isInputDropDown, setIsInputDropDown] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState<T.Search>({
    mainSearchTitle: '',
    detailSearchTitle: '',
  });

  const [date, setDate] = useState<T.Date>({
    startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });

  const [detailSearchValue, setDetailSearchValue] = useState<T.DetailSearchValue>();

  useEffect(() => {
    if (!isInputDropDown || searchValue.detailSearchTitle) return;
    setSearchValue(prev => ({ ...prev, detailSearchTitle: searchValue.mainSearchTitle }));
  }, [isInputDropDown]);

  const {
    data: FamilyEvents,
    total,
    isLoading,
    refetch,
  } = useGetFamilyEvents({
    page,
    dir,
    limit: PAGE_SIZE,
    name: detailSearchValue?.searchTitle ? detailSearchValue.searchTitle : undefined,
    startDate: detailSearchValue?.startDate,
    endDate: detailSearchValue?.endDate,
  });

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<number>();

  const onDelete = async () => {
    if (!deleteId) return;

    setDeleteLoading(true);
    const response = await deleteFamilyEvent({ path: { eventId: deleteId } });

    setDeleteLoading(false);
    if (!response) return;

    message.success('경조사가 삭제되었습니다.');
    refetch();
    setDeleteId(undefined);
  };

  const columns = useMemo<ColumnsType<T.FamilyEventTableData>>(() => {
    const columnsValue: ColumnsType<T.FamilyEventTableData> = [
      {
        key: 'type',
        dataIndex: 'type',
        title: '구분',
        width: '7%',
        render: type => {
          let eventType = '';
          switch (type) {
            case 'wedding':
              eventType = '결혼';
              break;
            case 'funeral':
              eventType = '부고';
              break;
            case 'opening':
              eventType = '개업';
              break;
            default:
              eventType = '-';
          }

          return <>{eventType}</>;
        },
      },
      {
        key: 'title',
        dataIndex: 'title',
        title: '경조명',
        ellipsis: true,
        width: '50%',
        render: title => {
          return <>{title}</>;
        },
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: '신청자',
        width: '10%',
        render: (_, { event_groupMembers }) => {
          const member = event_groupMembers.find(member => member.requester);
          if (!member) return <></>;

          const outstandingMonth = member.groupMember?.user?.kcplaaMember?.outstandingMonth;
          const payEntranceFee = member.groupMember?.user?.kcplaaMember?.payEntranceFee;
          const isDiligence =
            typeof outstandingMonth === 'number' && outstandingMonth < 6 && payEntranceFee;

          return (
            <S.ApplyMemberName
              {...(isDiligence && { title: '✓ 아이콘은 성실 회원을 표시합니다.' })}>
              {isDiligence && <C.CertifiedBadge />}
              {member.kcplaaCsMember?.name}
            </S.ApplyMemberName>
          );
        },
      },
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        // title: '등록일',
        title: () => {
          const toggleSort = () => setDir(dir === 'asc' ? 'desc' : 'asc');

          return <Sort title='등록일' dir={dir} onClick={toggleSort} />;
        },
        width: '13%',
        render: createdAt => {
          const createDate = dayjs(createdAt).format('YYYY.MM.DD HH.mm');
          return <>{createDate}</>;
        },
      },
      {
        key: 'approveStatus',
        dataIndex: 'approveStatus',
        title: '상태',
        width: '10%',
        render: approveStatus => {
          let status;
          switch (approveStatus) {
            case 'pending':
              status = '신청중';
              break;
            case 'approved':
              status = '승인';
              break;
            case 'rejected':
              status = '반려';
              break;
            default:
              status = '-';
          }
          return <>{status}</>;
        },
      },
      {
        key: 'approve',
        dataIndex: 'approve',
        title: '승인처리',
        width: '10%',
        render: (_, record) => {
          return (
            <Text
              underline={true}
              cursor='pointer'
              onClick={() => openCreateFamilyEventModal(record)}>
              상세보기
            </Text>
          );
        },
      },
      {
        key: 'actions',
        title: '',
        width: '20%',
        render: (_, record) => {
          return record.approveStatus === 'rejected' ? null : (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
              <Text
                underline={true}
                cursor='pointer'
                onClick={() => openCreateFamilyEventModal(record, true)}>
                수정
              </Text>
              <Text underline={true} cursor='pointer' onClick={() => setDeleteId(record.id)}>
                삭제
              </Text>
              {record.approveStatus === 'approved' ? (
                <Text
                  underline={true}
                  cursor='pointer'
                  onClick={() => {
                    console.log(record.updatedAt);
                    if (30 < dayjs().diff(record.updatedAt) / (1000 * 60)) {
                      openFamilyEventDetailsModal(record.id);
                    } else {
                      notification.error({
                        message: '메일/문자 전송 중에는 발송 현황조회가 불가능합니다.',
                      });
                    }
                  }}>
                  발송 현황
                </Text>
              ) : null}
            </div>
          );
        },
      },
    ];
    return columnsValue;
  }, [FamilyEvents]);

  const dataSource: T.FamilyEventTableDataSource = useMemo(() => {
    return FamilyEvents.map(v => ({ ...v, key: v.id }));
  }, [FamilyEvents]);

  const onSearch = () => {
    setPage(1);

    setDetailSearchValue({
      searchTitle: isInputDropDown ? searchValue.detailSearchTitle : searchValue.mainSearchTitle,
      startDate: isInputDropDown ? date.startDate + ' 00:00' : undefined,
      endDate: isInputDropDown ? date.endDate + ' 23:59' : undefined,
    });
  };

  const openCreateFamilyEventModal = (
    event: Get<GetFamilyEventsResponse['rows']>,
    isEditable?: boolean,
  ) => {
    const customTitle =
      event.type !== 'opening'
        ? event.title.replace('[訃音] ', '').replace('[결혼] ', '').split(` ${event.targetType}`)[0]
        : undefined;

    const initialFormData: CreateFamilyEvent = {
      customTitle,
      actionType: event.actionType,
      address: event.address,
      contacts: event.contacts,
      date: event.date,
      detailAddress: event.detailAddress,
      paymentAccount: event.paymentAccount || '',
      targetName: event.targetName,
      targetType: event.targetType,
      funeralDate: event.funeralDate || undefined,
      remarks: event.remarks || undefined,
      licenseNos: event.event_groupMembers
        .map(groupMember => groupMember.kcplaaCsMember?.licenseNo)
        .filter(licenseNo => licenseNo) as number[],
      fileIds: [],
      link: event.link || undefined,
    };

    dispatch(
      openModal({
        name: 'createFamilyEventModal',
        props: {
          initialFormData: {
            ...initialFormData,
            type: event.type,
            id: event.id,
          },
          initialFileName: event.attachedFiles.length ? event.attachedFiles[0].name : '',
          requestBySelf: event.requestBySelf,
          approveStatus: event.approveStatus,
          isEditable,
        },
      }),
    );
  };

  const openFamilyEventDetailsModal = (id: number) => {
    dispatch(openModal({ name: 'familyEventDetailsModal', props: { familyEventId: id } }));
  };

  useEffect(() => {
    on('SUCCESS_UPDATE_FAMILY_EVENT', refetch);

    return () => {
      off('SUCCESS_UPDATE_FAMILY_EVENT', refetch);
    };
  }, []);

  return (
    <>
      <S.ModalContainer
        isConfirm
        centered
        open={!!deleteId}
        width='440px'
        closable={false}
        footer={[
          <Button key='cancel' onClick={() => setDeleteId(undefined)}>
            취소
          </Button>,
          <Button key='delete' type='primary' loading={deleteLoading} onClick={() => onDelete()}>
            확인
          </Button>,
        ]}>
        <S.Container style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
          <p>선택하신 경조사를 삭제하시겠습니까?</p>
        </S.Container>
      </S.ModalContainer>

      <div>
        <S.InputWrapper ref={containerRef}>
          <Select
            showSearch
            open={isInputDropDown}
            autoClearSearchValue={false}
            placeholder={<div style={{ marginLeft: '20px' }}>회원 검색</div>}
            searchValue={searchValue.mainSearchTitle}
            onInputKeyDown={e => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                onSearch();
              }
            }}
            suffixIcon={
              <S.SuffixBox>
                <S.VerticalLine />
                <p
                  onClick={() => {
                    isInputClear.current = false;
                    setIsInputDropDown(prev => !prev);
                  }}>
                  상세
                </p>
              </S.SuffixBox>
            }
            onDropdownVisibleChange={e => {
              if (e) return;

              isInputClear.current = false;

              setIsInputDropDown(e);
            }}
            onFocus={() => {
              isInputClear.current = true;
            }}
            onSearch={mainSearchTitle => {
              if (!isInputClear.current) {
                isInputClear.current = true;
                return;
              }

              setSearchValue(prev => ({ ...prev, mainSearchTitle }));
            }}
            dropdownRender={() => {
              return (
                <S.DropdownWrapper
                  onMouseDown={e => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}>
                  <Input
                    value={searchValue.detailSearchTitle}
                    onChange={({ target: { value } }) => {
                      setSearchValue(prev => ({ ...prev, detailSearchTitle: value }));
                    }}
                    onPressEnter={e => {
                      if (e.nativeEvent.isComposing) return;
                      onSearch();
                    }}
                    allowClear
                    prefix={<SearchIcon />}
                    placeholder='회원 검색'
                  />
                  <div className='date-picker-wrapper'>
                    <DatePicker
                      allowClear={false}
                      value={dayjs(date.startDate)}
                      onChange={(_, startDate) => {
                        if (dayjs(startDate).isAfter(dayjs(date.endDate))) {
                          message.error('시작일이 종료일보다 늦을 수 없습니다.');
                          return;
                        }

                        setDate(prev => ({ ...prev, startDate }));
                      }}
                    />
                    <span style={{ margin: '0 8px' }}>~</span>
                    <DatePicker
                      allowClear={false}
                      value={dayjs(date.endDate)}
                      onChange={(_, endDate) => {
                        if (dayjs(endDate).isBefore(dayjs(date.startDate))) {
                          message.error('종료일이 시작일보다 빠를 수 없습니다.');
                          return;
                        }
                        setDate(prev => ({ ...prev, endDate }));
                      }}
                    />
                  </div>
                  <div className='search-button-wrapper'>
                    <Button type='primary' loading={isLoading} onClick={onSearch}>
                      검색
                    </Button>
                  </div>
                </S.DropdownWrapper>
              );
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: '324px',
              top: Number(containerRef.current?.offsetTop ?? 0) + 8,
            }}>
            <SearchIcon />
          </div>
        </S.InputWrapper>
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={isLoading}
          pagination={{
            total,
            position: ['bottomCenter'],
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
            onChange: setPage,
          }}
        />
      </div>
    </>
  );
}
export default FamilyEventTable;
